/**
 * IXVerify v2 — live barcode detection seam (ixv2 WP-3.2; plan §4).
 *
 * `zxing-wasm` over the getUserMedia viewfinder. The `BarcodeDetector` API is
 * NOT used (unavailable on iOS Safari — plan §4 says do not depend on it).
 * The client sends the RAW decoded payload to the server; GS1 AI parsing and
 * the barcode/vision cross-check live server-side (WP-1.4 ixvLabels — R6:
 * one parser, not two).
 *
 * Failure posture: zxing-wasm loads its WASM binary lazily; if the module or
 * binary cannot load (offline, CSP), live detection quietly degrades and the
 * shutter + server-vision path (the universal fallback, §4) carries label
 * reads. Nothing in the flow hard-depends on the decoder.
 */

type ReadBarcodesFn = (
  input: ImageData | Blob,
  options?: { formats?: string[]; maxNumberOfSymbols?: number; tryHarder?: boolean }
) => Promise<Array<{ text: string; format: string }>>;

let readerPromise: Promise<ReadBarcodesFn | null> | null = null;

/** Lazy-load the zxing reader once; null when unavailable (degrade, don't throw). */
export function loadBarcodeReader(): Promise<ReadBarcodesFn | null> {
  if (!readerPromise) {
    readerPromise = import('zxing-wasm/reader')
      .then((mod) => mod.readBarcodes as ReadBarcodesFn)
      .catch(() => null);
  }
  return readerPromise;
}

/** Test seam. */
export function __resetBarcodeReaderForTests(): void {
  readerPromise = null;
}

/** Formats worth scanning on IP kit labels (GS1 DataMatrix first — §4). */
const SCAN_FORMATS = ['DataMatrix', 'QRCode', 'Code128', 'Code39', 'EAN-13', 'ITF'];

/** Ignore a re-detection of the same payload inside this window (ms). */
export const DEDUPE_WINDOW_MS = 3000;
/** Frame sampling interval (ms) — fast enough to feel live, cheap enough for phones. */
export const SCAN_INTERVAL_MS = 280;
/** Downscale scan frames to this long edge (decode speed; full res is unnecessary). */
const SCAN_FRAME_MAX_EDGE = 1024;

export interface BarcodeScannerHandle {
  stop: () => void;
  /** True once the WASM reader is up and frames are being scanned. */
  isActive: () => boolean;
}

/**
 * Start a live scan loop over a playing <video>. Calls `onDetect(rawPayload)`
 * for each newly seen payload (deduped inside DEDUPE_WINDOW_MS). Returns a
 * handle; `stop()` is idempotent. If the reader cannot load, the loop never
 * starts and `isActive()` stays false — callers keep the shutter path.
 */
export function startBarcodeScanLoop(
  video: HTMLVideoElement,
  onDetect: (payload: string) => void,
  opts: { intervalMs?: number } = {}
): BarcodeScannerHandle {
  let stopped = false;
  let active = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const lastSeen = new Map<string, number>();
  const intervalMs = opts.intervalMs ?? SCAN_INTERVAL_MS;

  const canvas = document.createElement('canvas');

  async function scanOnce(read: ReadBarcodesFn) {
    if (stopped) return;
    try {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (vw && vh) {
        const scale = Math.min(1, SCAN_FRAME_MAX_EDGE / Math.max(vw, vh));
        const w = Math.max(1, Math.round(vw * scale));
        const h = Math.max(1, Math.round(vh * scale));
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
        if (ctx) {
          ctx.drawImage(video, 0, 0, w, h);
          const imageData = ctx.getImageData(0, 0, w, h);
          const results = await read(imageData, { formats: SCAN_FORMATS, maxNumberOfSymbols: 4 });
          const now = Date.now();
          for (const r of results || []) {
            const text = r?.text;
            if (!text) continue;
            const seenAt = lastSeen.get(text);
            if (seenAt != null && now - seenAt < DEDUPE_WINDOW_MS) continue;
            lastSeen.set(text, now);
            onDetect(text);
          }
        }
      }
    } catch {
      /* a bad frame must not kill the loop */
    }
    if (!stopped) timer = setTimeout(() => void scanOnce(read), intervalMs);
  }

  void loadBarcodeReader().then((read) => {
    if (!read || stopped) return;
    active = true;
    void scanOnce(read);
  });

  return {
    stop: () => {
      stopped = true;
      active = false;
      if (timer) clearTimeout(timer);
    },
    isActive: () => active,
  };
}
